// @flow
import React, { useCallback, useRef, useState } from "react";
import Button from "../Button";
import Camera from "../Icons/Camera";
import PaginationDots from "../PaginationDots";
import useMatchMediaMatches from "../../hooks/useMatchMediaMatches";
import useIsIntersectingWithRoot from "../../hooks/useIsIntersectingWithRoot";
import Lightbox from "../Lightbox";
import DesktopImageView from "./DesktopImageView";

import styles from "./imageGalleryGrid.module.scss";

type ImageData = {
  id: number,
  width: number,
  height: number,
  src: string
};

type Props = {
  images: Array<ImageData>,
  t?: (string, options: { [string]: string }, string) => string,
  showOneImageView?: boolean
};

const addHiddenClass = (imageCount, index, showOneImageView) => {
  if (showOneImageView || (imageCount > 3 && index > 2)) {
    return styles.hidden;
  }

  return "";
};

type ImageProps = {
  imageCount: number,
  index: number,
  image: ImageData,
  onImageClick: (number) => void,
  onButtonClick: () => void,
  onVisible: (number) => void,
  root: any,
  t?: (string, options: { [string]: string }, string) => string,
  showOneImageView: boolean
};

const Image = ({
  imageCount,
  index,
  image,
  onImageClick,
  onButtonClick,
  root,
  onVisible,
  t,
  showOneImageView
}: ImageProps) => {
  const ref = useRef();

  const onIntersecting = useCallback(() => {
    onVisible(index);
  }, [onVisible, index]);

  useIsIntersectingWithRoot(ref, root, 0.51, onIntersecting);

  return (
    <div
      className={`${styles.aspectRatioBox} ${
        index === 0 && (imageCount === 1 || imageCount >= 3) ? styles.big : ""
      } ${addHiddenClass(imageCount, index, showOneImageView)}`}
      ref={ref}
    >
      <button onClick={() => onImageClick(index)} type="button">
        <img
          src={image.src}
          width={image.width}
          height={image.height}
          className={styles.image}
          alt="Listing"
          loading="lazy"
        />
      </button>
      {index === 0 && imageCount > 3 && (
        <Button
          icon={<Camera size="1x" />}
          className={styles.moreBtn}
          onClick={onButtonClick}
          modifier="white-black"
        >
          <span>
            {t(
              "listingDetails:allPhotos",
              { imageCount },
              `All ${imageCount} photos`
            )}
          </span>
        </Button>
      )}
    </div>
  );
};

const ImageGalleryGrid = ({ images, t, showOneImageView }: Props) => {
  const ref = useRef();
  const [active, setActive] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const imageCount = images.length;

  const isGridLayout = useMatchMediaMatches("(min-width: 640px)") === true;
  const onVisible = useCallback(
    (index) => {
      if (isGridLayout) {
        return;
      }

      setActive(index);
    },
    [isGridLayout, setActive]
  );

  const onImageClick = useCallback(
    (index) => {
      setCurrentIndex(index);
      setIsOpen(true);
    },
    [setCurrentIndex, setIsOpen]
  );

  const onClose = useCallback(() => {
    setCurrentIndex(0);
    setIsOpen(false);
  }, [setCurrentIndex, setIsOpen]);

  const onButtonClick = useCallback(() => {
    setCurrentIndex(0);
    setIsOpen(true);
  }, [setCurrentIndex, setIsOpen]);

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.grid}>
        {showOneImageView && isGridLayout && (
          <DesktopImageView
            currentIndex={currentIndex}
            images={images}
            setIndex={(index) => setCurrentIndex(index)}
            onButtonClick={onButtonClick}
            showOneImageView={showOneImageView}
            onImageClick={onImageClick}
          />
        )}
        {images.map((image, index) => (
          <Image
            imageCount={imageCount}
            index={index}
            image={image}
            onImageClick={onImageClick}
            onButtonClick={onButtonClick}
            root={ref}
            onVisible={onVisible}
            key={image.id}
            t={t}
            showOneImageView={showOneImageView}
          />
        ))}
      </div>
      {imageCount > 1 && (
        <div className={styles.dotsContainer}>
          <PaginationDots count={imageCount} active={active} />
        </div>
      )}
      <Lightbox
        onClose={onClose}
        currentIndex={currentIndex}
        images={images}
        setIndex={(index) => setCurrentIndex(index)}
        isOpen={isOpen}
        portalParent="#portal"
      />
    </div>
  );
};

ImageGalleryGrid.defaultProps = {
  t: (key, options, defaultValue) => defaultValue,
  showOneImageView: false
};
export default ImageGalleryGrid;
